import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard, Users, CheckSquare, Activity, FileText,
  Megaphone, FlaskConical, BookOpen, BarChart2, Settings,
  LogOut, X, Sun, Moon, CalendarDays, ClipboardList, Radar,
} from 'lucide-react';
import { NucleoMark } from '@/components/ui/NucleoMark';
import { useAuthStore } from '@/stores/authStore';
import { useProjectStore } from '@/stores/projectStore';
import { authApi } from '@/api/auth';
import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '@/api/projects';
import { useTheme } from '@/hooks/useTheme';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, to: '/' },
  { label: 'Leads', icon: Users, to: '/leads' },
  { label: 'Buscar Leads', icon: Radar, to: '/prospect-search' },
  { label: 'Tareas', icon: CheckSquare, to: '/tasks' },
  { label: 'Agenda', icon: CalendarDays, to: '/agenda' },
  { label: 'Plan del Día', icon: ClipboardList, to: '/plan-del-dia' },
  { label: 'Actividad Diaria', icon: Activity, to: '/activity' },
  { label: 'Contenido', icon: FileText, to: '/content' },
  { label: 'Campañas', icon: Megaphone, to: '/campaigns' },
  { label: 'Experimentos', icon: FlaskConical, to: '/experiments' },
  { label: 'Aprendizajes', icon: BookOpen, to: '/learnings' },
  { label: 'Reportes', icon: BarChart2, to: '/reports' },
  { label: 'Configuración', icon: Settings, to: '/settings' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { user, logout, refreshToken } = useAuthStore();
  const { selectedProjectId, setSelectedProjectId } = useProjectStore();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
    staleTime: 60_000,
  });

  const handleLogout = async () => {
    if (refreshToken) {
      try { await authApi.logout(refreshToken); } catch { /* ignore */ }
    }
    logout();
    navigate('/login');
  };

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 flex h-full w-60 flex-col transition-transform duration-200',
          'lg:relative lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{ background: 'var(--nucleo-bg)', borderRight: '1px solid var(--nucleo-line)' }}
      >
        {/* Header — workspace mark + name */}
        <div
          className="flex items-center justify-between px-4 py-3.5"
          style={{ borderBottom: '1px solid var(--nucleo-line)' }}
        >
          <div className="flex items-center gap-2.5">
            <NucleoMark size={20} ring={false} />
            <div>
              <div className="text-sm font-medium tracking-tight" style={{ color: 'var(--nucleo-ink)', letterSpacing: '-0.2px' }}>Núcleo</div>
              <div className="font-mono text-[9.5px] uppercase tracking-[1px]" style={{ color: 'var(--nucleo-dim)' }}>Portafolio</div>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden" style={{ color: 'var(--nucleo-mute)' }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Project selector */}
        <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--nucleo-line)' }}>
          <select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value || null)}
            className="w-full px-3 py-1.5 text-xs outline-none cursor-pointer"
            style={{
              background: 'var(--nucleo-surface)',
              border: '1px solid var(--nucleo-line)',
              color: 'var(--nucleo-ink-soft)',
              fontFamily: 'inherit',
            }}
          >
            <option value="">Todos los proyectos</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navItems.map(({ label, icon: Icon, to }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                clsx('flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors', {
                  'border-l-2': isActive,
                })
              }
              style={({ isActive }) => ({
                background: isActive ? 'var(--nucleo-surface-sel)' : 'transparent',
                borderLeftColor: isActive ? 'var(--nucleo-accent)' : 'transparent',
                borderLeftWidth: '2px',
                paddingLeft: isActive ? '14px' : '16px',
                color: isActive ? 'var(--nucleo-ink)' : 'var(--nucleo-mute)',
              })}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User menu */}
        <div className="px-3 py-3" style={{ borderTop: '1px solid var(--nucleo-line)' }}>
          <div className="flex items-center gap-2 mb-2">
            <div
              className="h-6 w-6 flex items-center justify-center text-xs font-semibold flex-shrink-0"
              style={{ background: 'var(--nucleo-accent)', color: 'var(--nucleo-accent-ink)', borderRadius: '50%' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate" style={{ color: 'var(--nucleo-ink)' }}>{user?.name}</p>
              <p className="font-mono text-[9.5px] truncate uppercase tracking-[0.5px]" style={{ color: 'var(--nucleo-dim)' }}>{user?.role}</p>
            </div>
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? 'Modo claro' : 'Modo oscuro'}
              className="flex-shrink-0 flex items-center justify-center h-6 w-6 transition-colors"
              style={{ color: 'var(--nucleo-dim)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--nucleo-ink)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--nucleo-dim)'; }}
            >
              {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs transition-colors"
            style={{ color: 'var(--nucleo-mute)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--nucleo-ink)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--nucleo-surface)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--nucleo-mute)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <LogOut className="h-3.5 w-3.5" />
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}
